import { ResponsiveBar } from "@nivo/bar";
import { useGetList } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";

import type { B2CProfile, Deal } from "../types";
import {
  B2B_PILOT_STAGES,
  B2B_RECURRING_STAGES,
  B2C_STAGES,
} from "../deals/funnels";

type Subscription = {
  id: number;
  status: string;
  amount: number;
  currency: string;
  billing_interval: "month" | "year";
};

const formatMoneyByCurrency = (rows: Subscription[]) => {
  const totals = rows.reduce<Record<string, number>>((result, subscription) => {
    if (subscription.status !== "active") return result;
    const monthly =
      subscription.billing_interval === "year"
        ? Number(subscription.amount ?? 0) / 12
        : Number(subscription.amount ?? 0);
    result[subscription.currency] =
      (result[subscription.currency] ?? 0) + monthly;
    return result;
  }, {});
  return (
    Object.entries(totals)
      .map(([currency, amount]) =>
        new Intl.NumberFormat("es-CL", {
          style: "currency",
          currency,
          maximumFractionDigits: 0,
        }).format(amount),
      )
      .join(" · ") || "$0"
  );
};

const StageChart = ({
  title,
  subtitle,
  stages,
  deals,
  color,
}: {
  title: string;
  subtitle: string;
  stages: { value: string; label: string }[];
  deals: Deal[];
  color: string;
}) => {
  const data = stages.map((stage) => ({
    stage: stage.label,
    oportunidades: deals.filter((deal) => deal.stage === stage.value).length,
  }));
  const hasData = data.some((row) => row.oportunidades > 0);
  return (
    <Card>
      <CardContent className="pt-5">
        <h2 className="text-lg font-semibold">{title}</h2>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
        <div className="mt-4 h-[330px]">
          {hasData ? (
            <ResponsiveBar
              data={data}
              keys={["oportunidades"]}
              indexBy="stage"
              layout="horizontal"
              colors={[color]}
              margin={{ top: 8, right: 32, bottom: 36, left: 150 }}
              padding={0.28}
              valueScale={{ type: "linear", min: 0 }}
              indexScale={{ type: "band", round: true }}
              enableGridX
              enableGridY={false}
              enableLabel
              labelSkipWidth={14}
              labelTextColor="#ffffff"
              axisTop={null}
              axisRight={null}
              axisBottom={{ tickSize: 0, tickPadding: 8 }}
              axisLeft={{ tickSize: 0, tickPadding: 8 }}
              theme={{
                text: { fill: "var(--color-foreground)", fontSize: 11 },
                grid: {
                  line: {
                    stroke: "var(--color-border)",
                    strokeWidth: 1,
                  },
                },
                tooltip: {
                  container: {
                    background: "var(--color-popover)",
                    color: "var(--color-popover-foreground)",
                    borderRadius: 8,
                  },
                },
              }}
              tooltip={({ indexValue, value }) => (
                <div className="rounded-md border bg-popover p-2 text-sm text-popover-foreground shadow">
                  <strong>{indexValue}</strong>: {value}
                </div>
              )}
              role="img"
              ariaLabel={title}
            />
          ) : (
            <div className="flex h-full items-center justify-center rounded-lg border border-dashed text-sm text-muted-foreground">
              Aún no hay oportunidades en este funnel.
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export const FunnelOverview = () => {
  const dealsQuery = useGetList<Deal>("deals", {
    pagination: { page: 1, perPage: 1000 },
    filter: { "archived_at@is": null },
  });
  const profilesQuery = useGetList<B2CProfile>("b2c_profiles", {
    pagination: { page: 1, perPage: 1000 },
  });
  const subscriptionsQuery = useGetList<Subscription>("customer_subscriptions", {
    pagination: { page: 1, perPage: 1000 },
  });

  const deals = dealsQuery.data ?? [];
  const profiles = profilesQuery.data ?? [];
  const subscriptions = subscriptionsQuery.data ?? [];
  const pilotsWon = deals.filter((deal) => deal.stage === "b2b_won").length;
  const contractsWon = deals.filter(
    (deal) => deal.stage === "b2b_recurring_won",
  ).length;
  const b2cDeals = deals.filter(
    (deal) => deal.pipeline === "b2c_subscription",
  );
  const paidB2c = b2cDeals.filter((deal) => deal.stage === "b2c_paid").length;
  const conversion = b2cDeals.length
    ? `${((paidB2c / b2cDeals.length) * 100).toFixed(1)}%`
    : "0%";
  const activeSubscribers = profiles.filter(
    (profile) => profile.subscription_status === "active",
  ).length;

  const metrics = [
    ["Pilotos B2B ganados", pilotsWon],
    ["Contratos B2B ganados", contractsWon],
    ["Usuarios HoundTrade", profiles.length],
    ["Conversión B2C a pago", conversion],
    ["Suscripciones activas", activeSubscribers],
    ["MRR registrado", formatMoneyByCurrency(subscriptions)],
  ];

  return (
    <section className="space-y-4" aria-label="Funnels comerciales">
      <div>
        <h1 className="text-xl font-semibold">Funnels comerciales</h1>
        <p className="text-sm text-muted-foreground">
          Datos del workspace actualizados desde oportunidades, perfiles B2C y
          suscripciones.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border bg-border md:grid-cols-3 xl:grid-cols-6">
        {metrics.map(([label, value]) => (
          <div key={label} className="min-h-24 bg-background p-4">
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="mt-3 break-words text-xl font-semibold">{value}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <StageChart
          title="Conversión B2B a piloto pagado"
          subtitle="Cantidad actual por etapa; ganado exige SOW y setup pagado."
          stages={B2B_PILOT_STAGES}
          deals={deals.filter((deal) => deal.pipeline === "b2b_pilot")}
          color="#2f6f61"
        />
        <StageChart
          title="Expansión B2B a contrato recurrente"
          subtitle="Se crea automáticamente cuando el piloto queda ganado."
          stages={B2B_RECURRING_STAGES}
          deals={deals.filter((deal) => deal.pipeline === "b2b_recurring")}
          color="#8a6f3d"
        />
        <StageChart
          title="Conversión B2C gratis a pagado"
          subtitle="Cada email registrado en HoundTrade representa una oportunidad."
          stages={B2C_STAGES}
          deals={b2cDeals}
          color="#4f6f98"
        />
      </div>
    </section>
  );
};
